import { createContext, useCallback, useContext, useSyncExternalStore } from "react";

/** The dates a bar is being dragged to, before the gesture ends. */
export interface ScheduleDragPreview {
  issueId: number;
  start: string | null;
  due: string | null;
}

export interface ScheduleDragPreviewStore {
  get: () => ScheduleDragPreview | null;
  /** Null when the gesture ends, whichever way it ends. */
  set: (next: ScheduleDragPreview | null) => void;
  subscribe: (listener: () => void) => () => void;
}

/**
 * Where a Gantt bar is being dragged to, for surfaces other than the chart —
 * the open issue's panel shows its dates moving with the bar.
 *
 * A store outside React state on purpose: a pointer move is dozens of updates a
 * second, and page state would re-render the whole chart for each one. Only a
 * subscriber reading the dragged issue re-renders, and only when the dates it
 * would write actually change.
 */
export function createScheduleDragPreviewStore(): ScheduleDragPreviewStore {
  let current: ScheduleDragPreview | null = null;
  const listeners = new Set<() => void>();
  return {
    get: () => current,
    set: (next) => {
      if (
        current === next ||
        (current &&
          next &&
          current.issueId === next.issueId &&
          current.start === next.start &&
          current.due === next.due)
      ) {
        return;
      }
      current = next;
      for (const listener of listeners) listener();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

export const ScheduleDragPreviewContext = createContext<ScheduleDragPreviewStore | null>(null);

/** The store, for the chart that writes to it. Null outside a provider. */
export function useScheduleDragPreviewStore(): ScheduleDragPreviewStore | null {
  return useContext(ScheduleDragPreviewContext);
}

const NO_STORE_SUBSCRIBE = () => () => undefined;

/** The dates `issueId` is being dragged to right now, or null. */
export function useScheduleDragPreview(issueId: number): ScheduleDragPreview | null {
  const store = useContext(ScheduleDragPreviewContext);
  const read = useCallback(() => {
    const preview = store?.get() ?? null;
    return preview?.issueId === issueId ? preview : null;
  }, [store, issueId]);
  return useSyncExternalStore(store?.subscribe ?? NO_STORE_SUBSCRIBE, read, read);
}
